import { useEffect, useState } from "react";
import { useNavigate } from "@/hooks/useDeskNavigate";
import { toast } from "@/lib/toast";
import { InputOTP, InputOTPGroup, InputOTPSlot } from "@/components/ui/input-otp";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { ArrowRight, Mail, ShieldCheck, Wrench } from "lucide-react";
import Logo from "@/components/Logo";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

export default function Login() {
  const navigate = useNavigate();
  const { login } = useAuth();
  const [step, setStep] = useState("email"); // email | code
  const [email, setEmail] = useState(() => {
    // Pre-fill with the last address used so users don't retype it every time.
    try { return localStorage.getItem("ledesk_last_email") || ""; } catch { return ""; }
  });
  const [code, setCode] = useState("");
  const [loading, setLoading] = useState(false);
  const [devMode, setDevMode] = useState(false);
  const [devLoading, setDevLoading] = useState(false);

  useEffect(() => {
    api.get("/auth/dev/email-status")
      .then(({ data }) => setDevMode(!!data.dev_mode))
      .catch(() => {});
  }, []);

  const fetchDevOtp = async () => {
    if (!email.trim()) return;
    setDevLoading(true);
    try {
      const { data } = await api.get("/auth/dev/last-otp", {
        params: { email: email.trim().toLowerCase() },
      });
      setCode(data.code);
      toast.success("Code récupéré", { description: `${data.code}` });
    } catch (err) {
      toast.error("Aucun code disponible", {
        description: err.response?.data?.detail || "Cliquez d'abord sur « Recevoir le code ».",
      });
    } finally {
      setDevLoading(false);
    }
  };

  const submitEmail = async (e) => {
    e.preventDefault();
    if (!email.trim()) return;
    setLoading(true);
    try {
      await api.post("/auth/otp/request", { email: email.trim().toLowerCase() });
      toast.success("Code envoyé", {
        description: "Vérifiez votre boîte e-mail, valable 15 minutes",
      });
      setStep("code");
    } catch (err) {
      toast.error("Impossible d'envoyer le code", {
        description: err.response?.data?.detail || "Réessayez dans un instant.",
      });
    } finally {
      setLoading(false);
    }
  };

  const submitCode = async (e) => {
    e?.preventDefault();
    if (code.length !== 6) return;
    setLoading(true);
    try {
      const { data } = await api.post("/auth/otp/verify", {
        email: email.trim().toLowerCase(),
        code,
      });
      login(data.token, data.user);
      // Persist the successful address so the next visit prefills it.
      try { localStorage.setItem("ledesk_last_email", data.user.email); } catch {}
      toast.success(`Bonjour ${data.user.first_name}`, { description: "Bienvenue dans Le Desk" });
      navigate("/tableau-de-bord", { replace: true });
    } catch (err) {
      toast.error("Connexion refusée", {
        description: err.response?.data?.detail || "Code invalide ou expiré.",
      });
      setCode("");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen wallpaper flex relative" data-testid="login-page">
      {/* Short centered divider between the two columns — vertical gradient
          that fades from transparent at both ends to #444444 in the middle. */}
      <div
        aria-hidden="true"
        data-testid="login-column-divider"
        className="hidden lg:block absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 w-px"
        style={{
          height: "45vh",
          background:
            "linear-gradient(to bottom, transparent 0%, #444444 50%, transparent 100%)",
        }}
      />

      {/* Left brand panel */}
      <div className="hidden lg:flex flex-col justify-between w-1/2 p-14">
        <Logo size={56} withWordmark />
        <div className="max-w-md">
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-vertec-blue/15 text-vertec-blue text-xs uppercase tracking-widest mb-6 font-semibold">
            <ShieldCheck size={14} /> Connexion sécurisée par code
          </div>
          <h1 className="text-5xl font-bold leading-tight mb-4">
            Together,<br />
            <span style={{ color: "#0078D4" }}>we build better.</span>
          </h1>
        </div>
        <div className="text-xs text-muted-foreground">
          © {new Date().getFullYear()} Vertec
        </div>
      </div>

      {/* Right form panel */}
      <div className="flex-1 flex items-center justify-center p-8">
        <div className="w-full max-w-md">
          <div className="lg:hidden mb-8"><Logo size={48} withWordmark /></div>

          {step === "email" ? (
            <form onSubmit={submitEmail} className="space-y-6 animate-fade-in-up" data-testid="login-email-form">
              <div>
                <h2 className="text-3xl font-semibold mb-2">Se connecter</h2>
                <p className="text-muted-foreground text-sm">
                  Entrez votre e-mail — nous vous envoyons un code à 6 chiffres.
                </p>
              </div>

              <div className="space-y-2">
                <Label htmlFor="email" className="text-sm">Adresse e-mail</Label>
                <div className="relative">
                  <Mail className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" size={16} />
                  <Input
                    id="email"
                    type="email"
                    required
                    autoFocus
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="votre.email@exemple.com"
                    className="pl-9 h-11"
                    data-testid="login-email-input"
                  />
                </div>
              </div>

              <Button
                type="submit"
                disabled={loading}
                className="w-full h-11 bg-vertec-blue hover:bg-vertec-blueHover text-white font-medium gap-2"
                data-testid="login-request-code-btn"
              >
                {loading ? "Envoi…" : "Recevoir le code"}
                <ArrowRight size={16} />
              </Button>

              <p className="text-xs text-muted-foreground">
                Vous n'avez pas encore de compte ? Contactez un administrateur pour recevoir une invitation.
              </p>
            </form>
          ) : (
            <form onSubmit={submitCode} className="space-y-6 animate-fade-in-up" data-testid="login-code-form">
              <div>
                <h2 className="text-3xl font-semibold mb-3">Entrez le code</h2>
                <p className="text-muted-foreground text-sm mb-3">
                  Un code à 6 chiffres a été envoyé à cette adresse :
                </p>
                <div
                  className="inline-flex items-center gap-2 px-3 py-2 rounded-md bg-vertec-blue/10 border border-vertec-blue/30"
                  data-testid="login-email-display"
                >
                  <Mail size={14} className="text-vertec-blue" />
                  <span
                    className="text-foreground font-medium tracking-wide"
                    style={{ fontFamily: "Tahoma, Geneva, sans-serif", wordBreak: "break-all" }}
                  >
                    {email}
                  </span>
                </div>
              </div>

              <div className="flex justify-center py-4">
                <InputOTP
                  maxLength={6}
                  value={code}
                  onChange={(v) => setCode(v)}
                  onComplete={() => setTimeout(submitCode, 0)}
                  containerClassName="gap-3"
                  data-testid="login-otp-input"
                >
                  <InputOTPGroup className="gap-3">
                    {[0, 1, 2, 3, 4, 5].map((i) => (
                      <InputOTPSlot
                        key={i}
                        index={i}
                        className="w-12 h-14 text-xl font-semibold rounded-md border border-input first:rounded-md last:rounded-md first:border-l bg-background"
                        data-testid={`login-otp-slot-${i}`}
                      />
                    ))}
                  </InputOTPGroup>
                </InputOTP>
              </div>

              <Button
                type="submit"
                disabled={loading || code.length !== 6}
                className="w-full h-11 bg-vertec-blue hover:bg-vertec-blueHover text-white font-medium"
                data-testid="login-verify-code-btn"
              >
                {loading ? "Vérification…" : "Se connecter"}
              </Button>

              <div className="flex items-center justify-between text-sm">
                <button
                  type="button"
                  onClick={() => { setStep("email"); setCode(""); }}
                  className="text-muted-foreground hover:text-foreground"
                  data-testid="login-change-email-btn"
                >
                  Changer d'e-mail
                </button>
                <button
                  type="button"
                  onClick={submitEmail}
                  className="text-vertec-blue hover:underline"
                  data-testid="login-resend-code-btn"
                >
                  Renvoyer le code
                </button>
              </div>

              {devMode && (
                <div
                  className="mt-2 rounded-md border border-status-pending/40 bg-status-pending/10 p-3 flex items-start gap-3"
                  data-testid="dev-otp-helper"
                >
                  <Wrench size={16} className="text-status-pending mt-0.5 shrink-0" />
                  <div className="flex-1 min-w-0">
                    <div className="text-sm font-medium text-status-pending mb-0.5">
                      Mode développement (Resend non configuré)
                    </div>
                    <p className="text-xs text-muted-foreground mb-2">
                      Aucun e-mail n'est envoyé. Cliquez pour récupérer le code depuis le serveur.
                    </p>
                    <Button
                      type="button"
                      size="sm"
                      variant="outline"
                      className="border-status-pending/40 text-status-pending hover:bg-status-pending/10 h-8"
                      onClick={fetchDevOtp}
                      disabled={devLoading}
                      data-testid="dev-fetch-otp-btn"
                    >
                      {devLoading ? "Récupération…" : "Afficher le code"}
                    </Button>
                  </div>
                </div>
              )}
            </form>
          )}
        </div>
      </div>
    </div>
  );
}
